import React from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    Archive,
    LayoutDashboard,
    Calendar,
    Truck,
    Timer,
    FileText,
    Users,
    Briefcase,
    Shield,
    AlertCircle,
    Building2,
    Database,
    Megaphone,
    ExternalLink,
    Info
} from "lucide-react";

interface LegacyReportLink {
    name: string;
    path: string;
    icon: React.ReactNode;
    description: string;
    category: string;
}

const legacyReports: LegacyReportLink[] = [
    {
        name: "Clients",
        path: "/clients",
        icon: <LayoutDashboard size={18} />,
        description: "Client overview and metrics dashboard",
        category: "Overview"
    },
    {
        name: "Client Tenure",
        path: "/client-tenure",
        icon: <Calendar size={18} />,
        description: "Client relationship duration analysis",
        category: "Overview"
    },
    {
        name: "Delivery",
        path: "/delivery",
        icon: <Truck size={18} />,
        description: "Service delivery metrics and performance",
        category: "Operations"
    },
    {
        name: "Timekeeping",
        path: "/timekeeping",
        icon: <Timer size={18} />,
        description: "Time tracking and utilization reports",
        category: "Operations"
    },
    {
        name: "Work Activity",
        path: "/work-activity",
        icon: <FileText size={18} />,
        description: "Work activity breakdown and analysis",
        category: "Operations"
    },
    {
        name: "Partners",
        path: "/partners",
        icon: <Users size={18} />,
        description: "Partner performance and relationships",
        category: "Operations"
    },
    {
        name: "Services",
        path: "/service-reports",
        icon: <Briefcase size={18} />,
        description: "Service catalog and utilization",
        category: "Operations"
    },
    {
        name: "AML Review",
        path: "/review-companies",
        icon: <Shield size={18} />,
        description: "Anti-money laundering review status",
        category: "Compliance"
    },
    {
        name: "Expired IDs",
        path: "/expired-ids",
        icon: <AlertCircle size={18} />,
        description: "Expired identification documents",
        category: "Compliance"
    },
    {
        name: "CRO Discrepancies",
        path: "/cro-discrepancies",
        icon: <Building2 size={18} />,
        description: "Companies Registration Office data mismatches",
        category: "Compliance"
    },
    {
        name: "CRO Data Dump",
        path: "/cro-data-dump",
        icon: <Database size={18} />,
        description: "Raw CRO data export and analysis",
        category: "Compliance"
    },
    {
        name: "Marketing",
        path: "/marketing",
        icon: <Megaphone size={18} />,
        description: "Marketing campaigns and analytics",
        category: "Growth"
    },
    {
        name: "Sales",
        path: "/sales",
        icon: <Briefcase size={18} />,
        description: "Sales pipeline and conversion metrics",
        category: "Growth"
    },
    {
        name: "Caching",
        path: "/metadata",
        icon: <Database size={18} />,
        description: "Cache management and changelog",
        category: "System"
    }
];

const LegacyReports: React.FC = () => {
    const categories = ["Overview", "Operations", "Compliance", "Growth", "System"];

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <Archive size={20} />
                    <h1>Legacy Reports</h1>
                </div>
            </div>

            <div className="legacy-warning-card">
                <div className="legacy-warning-header">
                    <AlertTriangle size={24} />
                    <h2>Important Notice</h2>
                </div>
                <div className="legacy-warning-content">
                    <p>
                        The reports listed below were built for earlier versions of <strong>analytics.kinore.com</strong>. 
                        While they remain accessible, please be aware of the following:
                    </p>
                    <ul>
                        <li>
                            <Info size={14} />
                            <span>Some reports may not render correctly with the current theme or layout</span>
                        </li>
                        <li>
                            <Info size={14} />
                            <span>Data sources may point to legacy database entries that are no longer actively maintained</span>
                        </li>
                        <li>
                            <Info size={14} />
                            <span>Certain features or interactive elements may not function as expected</span>
                        </li>
                        <li>
                            <Info size={14} />
                            <span>These reports are scheduled for migration to the new reporting framework</span>
                        </li>
                    </ul>
                    <p className="legacy-warning-note">
                        If you encounter issues or require updated versions of these reports, 
                        please contact <a href="mailto:ITSupport@kinore.com">ITSupport@kinore.com</a>.
                    </p>
                </div>
            </div>

            <div className="legacy-reports-grid">
                {categories.map(category => {
                    const categoryReports = legacyReports.filter(r => r.category === category);
                    if (categoryReports.length === 0) return null;
                    
                    return (
                        <div key={category} className="legacy-category">
                            <h3 className="legacy-category-title">{category}</h3>
                            <div className="legacy-reports-list">
                                {categoryReports.map(report => (
                                    <Link 
                                        key={report.path} 
                                        to={report.path} 
                                        className="legacy-report-card"
                                    >
                                        <div className="legacy-report-icon">
                                            {report.icon}
                                        </div>
                                        <div className="legacy-report-info">
                                            <span className="legacy-report-name">{report.name}</span>
                                            <span className="legacy-report-desc">{report.description}</span>
                                        </div>
                                        <ExternalLink size={14} className="legacy-report-arrow" />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default LegacyReports;
